import React from 'react'
import { Helmet } from 'react-helmet-async'

import MetaImg from '@app/ui/assets/meta/meta-img.png'
import AppleTouchIcon from '@app/ui/assets/meta/fav/apple-touch-icon.png'
import Favicon32 from '@app/ui/assets/meta/fav/favicon-32x32.png'
import Favicon16 from '@app/ui/assets/meta/fav/favicon-16x16.png'
import SafariPinnedTab from '@app/ui/assets/meta/fav/safari-pinned-tab.svg'
import { runtimeConfig } from '@app/common/config'
import { isTestEnvironment } from '@app/utils/environmentUtils'

interface MetaProps {
  children?: React.ReactNode
  title: string
  description?: string
  canonical?: string
  image?: string
}

const googleAnalyticsId = runtimeConfig.joinSproutTherapyConfig?.GA_ID // 'UA-157564127-2'
const googleTagManagerId = runtimeConfig.joinSproutTherapyConfig?.GTM_ID // 'GTM-M8L77GG'
const googleAdsId = runtimeConfig.joinSproutTherapyConfig?.GADS_ID // 'AW-658106055'
const facebookPixelId = runtimeConfig.joinSproutTherapyConfig?.FB_ID // '1611063472374137'
const thesisPixelId = runtimeConfig.joinSproutTherapyConfig?.THESIS_ID // 'ny5t8y46'

// Prod - QqQzADrbeQ8EQwhqEm7wI4w06NKZ5BjR, Dev - POe4vnCmvWzGDMK0MduB2IrVyEGHzHiC
const segmentApiKey = isTestEnvironment() ? runtimeConfig.SEGMENT_API_KEY_DEV : runtimeConfig.SEGMENT_API_KEY

export const Meta = ({ children, title, description, canonical, image }: MetaProps) => (
  <Helmet>
    <meta name="viewport" content="width=device-width,minimum-scale=1,initial-scale=1"></meta>
    <meta charSet="utf-8"></meta>
    <link rel="apple-touch-icon" sizes="180x180" href={AppleTouchIcon} />
    <link rel="icon" type="image/png" sizes="32x32" href={Favicon32} />
    <link rel="icon" type="image/png" sizes="16x16" href={Favicon16} />
    <link rel="mask-icon" href={SafariPinnedTab} color="#f6efdb" />
    <meta name="msapplication-TileColor" content="#004445" />
    <meta name="theme-color" content="#fffcf4"></meta>

    <title>{title}</title>
    {description && <meta name="description" content={description} />}
    {canonical && <link rel="canonical" href={canonical} />}

    <meta property="og:type" content="website" />
    <meta name="og:title" property="og:title" content={title} />
    <meta property="og:site_name" content="Elemy" />
    <meta property="og:image" content={image || MetaImg} />
    {description && <meta name="og:description" property="og:description" content={description} />}
    {canonical && <meta property="og:url" content={canonical} />}

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta property="twitter:image" content={image || MetaImg} />

    {children}
    <script>
      {`
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics._writeKey="${segmentApiKey}";analytics.SNIPPET_VERSION="4.13.2";
        analytics.load("${segmentApiKey}");
        analytics.page();
        }}();
      `}
    </script>
    {/* Global site tag (gtag.js) - Google Analytics */}
    <script async src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`} />
    <script>
      {/* Google Tag Manager */}
      {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${googleTagManagerId}');
      `}
    </script>
    <script>
      {`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${googleAnalyticsId}', {
          page_path: window.location.pathname,
        });
        gtag('config', '${googleAdsId}');
      `}
    </script>
    <script>
      {`
        (function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
        (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
        m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
        })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
  
        ga('create', '${googleAnalyticsId}', 'auto');
      `}
    </script>
    <script>
      {`
        !function(f,b,e,v,n,t,s)
        {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
        n.callMethod.apply(n,arguments):n.queue.push(arguments)};
        if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
        n.queue=[];t=b.createElement(e);t.async=!0;
        t.src=v;s=b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t,s)}(window, document,'script',
        'https://connect.facebook.net/en_US/fbevents.js');
        fbq('init', '${facebookPixelId}');
        fbq('track', 'PageView');
      `}
    </script>
    <noscript>
      {`<img height='1' width='1' alt='danger-set' style='display:none' src='https://www.facebook.com/tr?id=${facebookPixelId}&ev=PageView&noscript=1'/>`}
    </noscript>
    <script>
      {`
        !function(t,h,i,x,e,l){if(t.thix)return;x=t.thix=function(){x.callMethod?x.callMethod.apply(x,arguments):x.queue.push(arguments);},x.queue=[],x.t=1*new Date(),e=h.createElement('script'),e.type='text/javascript',e.async=!0,e.src=i,l=h.getElementsByTagName('script')[0],l.parentNode.insertBefore(e,l);}(window,document,'https://autismcare.elemy.com/thixel.js');
        thix('init', '${thesisPixelId}');
        thix('event', 'page.load');
      `}
    </script>
  </Helmet>
)
